import axiosInstance, { axiosInstanceFast } from "./axiosInstance";

interface PostFormDataOptions {
  url: string;
  data: Record<string, any>;
  csrfToken?: string;
  signal?: AbortSignal;
}

interface UploadFileOptions {
  url: string;
  formData: FormData;
  signal?: AbortSignal;
}

interface PostDynamicFormDataOptions {
  urlTemplate: string; // e.g. "/import-parser/:prodname/:ip/"
  pathParams: Record<string, string | undefined>; // { prodname: 'ABC', ip: '1.2.3.4' }
  formFields: Record<string, any>; // files or values to send
  csrfToken?: string;
  signal?: AbortSignal;
}

export const fetchSyslogDevices = async (signal?: AbortSignal) => {
  const payload = { tab: "" }; // define the payload here
  const response = await axiosInstance.post("/getShadowEngineData/", payload, {
    signal,
  });
  return response.data;
};

export const fetchPlugins = async (id: string, signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get(`/plugin/get-info/${id}`, {
    signal,
  });
  return response.data;
};

export const fetchPluginConfig = async (id: string, signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get(
    `/plugin/get-config-page/${id}`,
    {
      signal,
    },
  );
  return response.data;
};

export const fetchPluginsUIPage = async (id: string, signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get(`/plugin/get-ui-page/${id}`, {
    signal,
  });
  return response.data;
};

export const PluginActivateDeactivate = async (
  plugin_id: string,
  data: {
    status: number;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.post(
    `plugin/plugin-activate/${plugin_id}`,
    data,
    { signal },
  );
  return response.data;
};

export const pluginCreateConfig = async (
  plugin_id: string,
  data: any,
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.post(
    `/plugin/create-config-page/${plugin_id}`,
    data,
    { signal },
  );
  return response.data;
};

export const pluginConfigDeleteAPI = async (
  plugin_id: string,
  data: {
    tab_id: string;
    uid: string;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.delete(
    `/plugin/delete-config-page/${plugin_id}`,
    { data, signal },
  );
  return response.data;
};

export const pluginAllDeleteAPI = async (
  plugin_id: string,
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.delete(
    `/plugin/delete-all-page/${plugin_id}`,
    { signal },
  );
  return response.data;
};

export const fetchVisualData = async (signal?: AbortSignal) => {
  const payload = { tab: "visual" }; // define the payload here
  const response = await axiosInstance.post("/getShadowEngineData/", payload, {
    signal,
  });
  return response.data;
};

export const fetchSavedSearchData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.get("/shadow360-saved-searches/", {
    signal,
  });
  return response.data;
};

export const fetchDataListData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.get("/shadow360GetSearchList/", {
    signal,
  });
  return response.data;
};

export const fetchLogMappingFields = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post(
    "/get-all-log-mappings/",
    {},
    {
      signal,
    },
  );
  return response.data;
};

export const fetchDeviceSetting = async (
  name?: string,
  deviceType?: string,
  deviceVendor?: string,
  deviceProduct?: string,
  ip?: string,
  port?: string,
  multiprodDevice?: string,
  syslogSource?: string,
  deviceDistributedSystem?: string,
  epochTime?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      name: name,
      deviceType: deviceType,
      deviceVendor: deviceVendor,
      deviceProduct: deviceProduct,
      ip: ip,
      port: port,
      multiprodDevice: multiprodDevice,
      syslogSource: syslogSource,
      deviceDistributedSystem: deviceDistributedSystem,
      epochTime: epochTime,
    };
    const response = await axiosInstance.post("/devicesSettings/", payload, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchEventData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post(
    "/get-alleventdata/",
    {},
    {
      signal,
    },
  );
  return response.data;
};

export const fetchAnomalyData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post("/get-allanomalydata/", {
    signal,
  });
  return response.data;
};

export const fetchMetaData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post(
    "/get-allmetasavingdata/",
    {},
    {
      signal,
    },
  );
  return response.data;
};

export const fetchSavedReportData = async (signal?: AbortSignal) => {
  const response = await axiosInstance.get("/getReports/", {
    signal,
  });
  return response.data;
};

export const fetchDashboardList = async (signal?: AbortSignal) => {
  const response = await axiosInstance.get("/getDashboardList/", {
    signal,
  });
  return response.data;
};

export const fetchCustomDashboard = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get(
    "/system/settings/basic-settings/",
    {
      signal,
    },
  );
  return response.data;
};

export const fetchSyslogParserSettings = async (
  device_ip?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      device_ip: device_ip,
    };
    const response = await axiosInstance.post(
      "/getSyslogParserSettings/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};
export const refreshSyslogParserSampleDb = async (
  device_ip?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      device_ip: device_ip,
    };
    const response = await axiosInstance.post(
      "/refreshSyslogParserSampleDb/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchSyslogParserLog = async (
  row_id?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      row_id: row_id,
    };
    const response = await axiosInstance.post("/getSyslogParserLog/", payload, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchDetectedFieldTypes = async (
  payload?: string,
  signal?: AbortSignal,
) => {
  try {
    const response = await axiosInstance.post(
      "/fastapi/syslog/predict-patterns",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchSampleLogsParser = async (
  row_id: number,
  row_log: string,
  device_ip: string,
  keyword: string,
  logparsetype: string,
  splitBy: string,
  assignmentOp: string,
  split_after: string,
  sec_split: string,
  third_split: string,
  device_type: string,
  product_name: string,
  product_vendor: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      row_id,
      row_log,
      device_ip,
      keyword,
      logparsetype,
      splitBy,
      assignmentOp,
      split_after,
      sec_split,
      third_split,
      device_type,
      product_name,
      product_vendor,
    };
    const response = await axiosInstance.post(
      "/getSampleLogsParser/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const cleanUpFieldValue = async (
  row_id: number,
  position: number,
  fieldVal: string,
  operation: string,
  parseby: string,
  assignmentOperator: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      row_id,
      position,
      fieldVal,
      operation,
      parseby,
      assignmentOperator,
    };
    const response = await axiosInstance.post("/cleanUpFieldValue/", payload, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const saveSyslogParserSettings = async (
  payload: any,
  signal?: AbortSignal,
) => {
  try {
    const response = await axiosInstance.post(
      "/saveSyslogParserSettings/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchResolvedIps = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post("/getResolvedIps/", {
    signal,
  });

  return response.data;
};

export const deleteEntry = async (
  type: string,
  epoch: string | null | number,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      type: type,
      epoch: epoch,
    };
    const response = await axiosInstance.post("/deleteEntry/", payload, {
      signal,
    });
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const deleteSingleLog = async (row_id: string, signal?: AbortSignal) => {
  try {
    const payload = {
      row_id: row_id,
    };
    const response = await axiosInstance.post(
      "/deleteSingleLogFromSyslogSampleDb/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const deleteAllLog = async (
  device_ip: string,
  parsed: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      device_ip: device_ip,
      parsed: parsed,
    };
    const response = await axiosInstance.post(
      "/deleteLogsFromSyslogSampleDb/",
      payload,
      {
        signal,
      },
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const getClassification = async () => {
  const response = await axiosInstance.get(
    "/static/ls/json/syslog-field-mapping.json",
  );
  return response.data;
};

export const getSyslogDeviceTypeDynamicFields = async (
  signal?: AbortSignal,
) => {
  const params = {
    JsonFilePath: "/opt/common_modules/json/SyslogDeviceTypeDynamicFields.json",
  };
  const response = await axiosInstanceFast.post(
    "/entity/json-file-loader",
    params,
    { signal },
  );
  return response.data;
};

export const changeAnomalyStatus = async (
  anomalyId?: string,
  newStatus?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      anomalyId: anomalyId,
      anomalyNewStatus: newStatus,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/changeAnomalyStatus/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const deleteAnomaly = async (
  anomalyId: string | number,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      anomalyId: anomalyId,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/deleteAnomaly/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const changeMetaStatus = async (
  metaId?: string,
  newStatus?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      metaId: metaId,
      metaNewStatus: newStatus,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/changeMetaStatus/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const deleteMeta = async (
  eventId: string | number,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      eventId: eventId,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/deleteMetaSaving/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const changeEventStatus = async (
  eventId?: string,
  newStatus?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      eventId: eventId,
      eventNewStatus: newStatus,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/changeEventStatus/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const deleteEvent = async (
  eventId: string | number,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      eventId: eventId,
    };
    const response = await axiosInstance.post(
      "/usecase-builder/deleteEvent/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const changeDashboardStatus = async (
  currentStatus?: string | number,
  dashboard?: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      currentStatus: currentStatus,
      dashboard: dashboard,
    };
    const response = await axiosInstance.post(
      "/customDashboardStatus/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const uploadGzipFile = async ({
  url,
  formData,
  signal,
}: {
  url: string;
  formData: FormData;
  signal?: AbortSignal;
}) => {
  try {
    const response = await axiosInstance.post(url, formData, {
      signal,
    });

    return response.data;
  } catch (error) {
    console.error("Error while importing:", error);
    throw error;
  }
};

export const exportEAMDataApiCall = async (
  exportUrl: string,
  exportData: any,
  signal?: AbortSignal,
) => {
  try {
    const response = await axiosInstance.post(exportUrl, exportData, {
      signal,
    });

    return response;
  } catch (error) {
    console.error("Error during export API call:", error);
    throw error;
  }
};

export const deleteReport = async (
  report_type: string,
  created_on: string,
  uqid: string,
  download_type: string,
  save_type: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      report_type,
      created_on,
      uqid,
      download_type,
      save_type,
    };
    const response = await axiosInstance.post("/deleteReport/", payload, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const downloadSavedReport = async ({
  url,
  fileName,
}: {
  url: string;
  fileName: string;
}) => {
  try {
    const response = await axiosInstance.get(url, {
      responseType: "blob",
    });

    if (response.status === 200) {
      const contentType = response.headers["content-type"];
      let fileExtension = "pdf"; // Default
      if (contentType.includes("application/pdf")) {
        fileExtension = "pdf";
      } else if (contentType.includes("text/plain")) {
        fileExtension = "txt";
      } else if (contentType.includes("application/zip")) {
        fileExtension = "zip";
      } else if (contentType.includes("csv/force-download")) {
        fileExtension = "csv";
      } else if (contentType.includes("pdf/force-download")) {
        fileExtension = "pdf";
      }

      const blob = new Blob([response.data], { type: contentType });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${fileName}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl); // Cleanup
    } else {
      throw new Error("Download failed with status " + response.status);
    }
  } catch (error) {
    console.error("Download failed:", error);
    throw error;
  }
};

// export const createEmailReport = async (payload: any, signal?: AbortSignal) => {
//   return axiosInstance.post("/reportSettings/", payload, {
//     signal,
//   });
// };
export const createEmailReport = async (payload: any, signal?: AbortSignal) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(key, String(v)));
    } else if (typeof value === "object" && value !== null) {
      formData.append(key, JSON.stringify(value));
    } else if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  return axiosInstance.post("/reportSettings/", formData, { signal });
};

export const scheduleDownloadReport = async (
  payload: any,
  signal?: AbortSignal,
) => {
  return axiosInstance.post("/DownloadReport/", payload, {
    signal,
  });
};

export const deleteWidget = async ({
  widgetName,
  widgetType,
  signal,
}: {
  widgetName: string;
  widgetType: string;
  signal?: AbortSignal;
}) => {
  const payload = {
    widgetName,
    widgetType,
  };

  const response = await axiosInstance.post("/deleteWidget/", payload, {
    signal,
  });

  return response.data;
};

export const uploadFile = async ({
  url,
  formData,
  signal,
}: UploadFileOptions) => {
  const response = await axiosInstance.post(url, formData, {
    signal,
  });
  return response.data;
};

export const postFormData = async ({
  url,
  data,
  signal,
}: PostFormDataOptions) => {
  const formData = new FormData();
  for (const key in data) {
    formData.append(key, data[key]);
  }
  const response = await axiosInstance.post(url, formData, {
    signal,
  });
  return response.data;
};

// utils/apiClient.ts

export const postDynamicFormData = async ({
  urlTemplate,
  pathParams,
  formFields,
  csrfToken,
  signal,
}: PostDynamicFormDataOptions) => {
  // Build the URL by replacing params
  let finalUrl = urlTemplate;
  for (const key in pathParams) {
    const encoded = encodeURIComponent(pathParams[key] || "");
    finalUrl = finalUrl.replace(`:${key}`, encoded);
  }

  // Create FormData
  const formData = new FormData();
  for (const key in formFields) {
    formData.append(key, formFields[key]);
  }
  const response = await axiosInstance.post(finalUrl, formData, {
    headers: {
      ...(csrfToken && { "X-CSRFToken": csrfToken }),
      "Content-Type": "multipart/form-data",
    },
    signal,
  });
  return response.data;
};

export const editWidget = async (
  widgetname: string,
  widgetType: string,
  signal?: AbortSignal,
) => {
  try {
    const payload = {
      widgetType,
      visName: widgetname,
    };
    const response = await axiosInstance.post(
      "/getVisualizationData/",
      payload,
      {
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const fetchReportPreview = async (filePath: string): Promise<Blob> => {
  const response = await axiosInstance.get(filePath, { responseType: "blob" });
  return response.data;
};

export const fetchCustomDashboardList = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get(
    "/system/custom-dashboard-urls",
    {
      signal,
    },
  );
  return response.data.data;
};

export const fetchCustomWidgetList = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get("/system/custom-widgets", {
    signal,
  });
  return response.data.data;
};

export const anomalyListWithType = async (
  data: {
    CustomFilter: string;
    filterTime: string;
    fromDate?: number;
    toDate?: number;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.post(
    "anomaly/incident-list-with-type",
    data,
    { signal },
  );
  return response.data.data;
};

export const usecasesCustomFilter = async (
  data: {
    CustomFilter: string;
    fromDate?: number;
    toDate?: number;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.post("usecasesCustomFilter", data, {
    signal,
  });
  return response.data.data;
};

export const exportWidgets = async (params: string[], signal?: AbortSignal) => {
  const formData = new FormData();
  formData.append("params", JSON.stringify(params));

  return axiosInstance.post("/export-widgets/", formData, {
    signal,
    responseType: "blob",
  });
};

export const siemSettings = async (payload: any, signal?: AbortSignal) => {
  return axiosInstance.post("/siemSettings/", payload, {
    signal,
  });
};

export const testSiemSettings = async (payload: any, signal?: AbortSignal) => {
  return axiosInstance.post("/testsiemconnectivity/", payload, {
    signal,
  });
};

export const DiscoverSiemSettings = async (
  payload: any,
  signal?: AbortSignal,
) => {
  return axiosInstance.post("/discoverQradar/", payload, {
    signal,
  });
};

export const SaveDiscoverQradar = async (
  payload: any,
  signal?: AbortSignal,
) => {
  return axiosInstance.post("/saveDiscoverQradar/", payload, {
    signal,
  });
};

export const fetchAgentConfig = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get("/ls-agents-ui/fetch-config/", {
    signal,
  });
  return response.data;
};

export const updateAgentsConfig = async (
  config: {
    features: Record<string, { enabled: number; label?: string }>;
    eventlogger?: Array<{ name: string; enable: number }>;
    etwlogger?: Array<{ name: string; enable: number }>;
    sslkeycollector?: { path: string };
    [key: string]: any;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.put(
    "/ls-agents-ui/update-config/",
    config,
    {
      signal,
    },
  );
  return response.data;
};

export const checkAgentFileExists = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get("ls-agents-ui/home-server-ip/", {
    signal,
  });

  const data = response?.data;
  const homeServerIp =
    data?.home_server_ip ?? data?.data?.home_server_ip ?? data?.homeServerIp;

  if (typeof homeServerIp === "string") {
    return homeServerIp.trim().length > 0;
  }

  return false;
};

export const downloadAgent = async (options?: {
  signal?: AbortSignal;
  generateFile?: boolean;
  password?: string;
  ip?: string;
}) => {
  try {
    const payload = new URLSearchParams();
    if (typeof options?.generateFile === "boolean") {
      payload.append("generateFile", String(options.generateFile));
    }
    if (options?.password) {
      payload.append("password", options.password);
    }
    if (options?.ip?.trim()) {
      payload.append("ip", options.ip.trim());
    }

    const response = await axiosInstance.post(
      "/ls-agent/download-agent/",
      payload,
      {
        responseType: "blob",
        signal: options?.signal,
      },
    );

    if (response.status === 200) {
      const contentType = String(response.headers?.["content-type"] || "");
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        let detailMsg = "Failed to download agent";
        try {
          const detail = JSON.parse(text);
          detailMsg = detail?.msg || detail?.detail || detailMsg;
        } catch (_e) {
          detailMsg = text || detailMsg;
        }
        throw new Error(detailMsg);
      }

      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const disposition = String(
        response.headers?.["content-disposition"] || "",
      );
      const fileNameMatch = disposition.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i,
      );
      a.download = fileNameMatch?.[1] || "LinkShadowAgent.msi";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } else {
      throw new Error("Download failed with status " + response.status);
    }
  } catch (error: any) {
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const detail = JSON.parse(text);
        const parsedError = new Error(
          detail?.msg || detail?.detail || "Agent download failed",
        );
        throw parsedError;
      } catch (_e) {
        const fallbackText = await error.response.data.text();
        throw new Error(fallbackText || "Agent download failed");
      }
    }

    console.error("Agent download failed:", error);
    throw error;
  }
};

export const fetchAgentsDevices = async (signal?: AbortSignal) => {
  const response = await axiosInstanceFast.get("/ls-agents-ui/list/", {
    signal,
  });
  return response.data;
};

export const fetchAgentConfigById = async (
  id: number,
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.get(
    `/ls-agents-ui/fetch-config/${id}/`,
    {
      signal,
    },
  );

  return response.data;
};

export const updateAgentConfigById = async (
  id: number,
  config: {
    features: Record<string, { enabled: number; label?: string }>;
    eventlogger?: Array<{ name: string; enable: number }>;
    etwlogger?: Array<{ name: string; enable: number }>;
    sslkeycollector?: { path: string };
    [key: string]: any;
  },
  signal?: AbortSignal,
) => {
  const response = await axiosInstanceFast.put(
    `/ls-agents-ui/update-config/${id}/`,
    config,
    {
      signal,
    },
  );

  return response.data;
};
